// AdminCoPo.jsx - Updated version
import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import API from "../../../utils/API";
import FinalCOPOView from "./Views/FinalCoPoView";
import OutcomesView from "./Views/OutcomesView";
import MatrixView from "./Views/MatrixView";
import AdminHeader from "../AdminHeader";
import { ChevronLeft } from "lucide-react";
import { useThemeContext } from "../../../context/ThemeContext";
import FiltersSection from "../../DynamicComponents/CO-PO Attainment/FiltersSection";
import CourseList from "../../DynamicComponents/CO-PO Attainment/CourseList";
import CourseHeader from "./Components/CourseHeader";
import SectionsTable from "./Components/SectionsTable";
import ActionButtons from "./Components/ActionButtons";

const AdminCOPO = () => {
  const { darkMode } = useThemeContext();

  const [semesterType, setSemesterType] = useState("even");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeView, setActiveView] = useState("final");
  const [isLoading, setIsLoading] = useState(false);
  const [showCourseList, setShowCourseList] = useState(true);
  const [courseOutcomes, setCourseOutcomes] = useState([]);
  const [programOutcomes, setProgramOutcomes] = useState([]);
  const [programSpecificOutcomes, setProgramSpecificOutcomes] = useState([]);
  const [articulationMatrix, setArticulationMatrix] = useState(null);
  const [isOutcomesLoading, setIsOutcomesLoading] = useState(false);
  const [attainmentData, setAttainmentData] = useState(null);
  const [isAttainmentLoading, setIsAttainmentLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const defaultAcademicYear = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);

  const academicYears = [
    defaultAcademicYear,
    `${currentYear + 1}-${(currentYear + 2).toString().slice(-2)}`,
  ];

  // Fetch attainment data
  const fetchAttainmentData = async () => {
    if (!selectedCourse || !academicYear || !semesterType) return;

    setIsAttainmentLoading(true);
    try {
      const response = await API.get("/ece/admin/attainment-status", {
        params: {
          course_code: selectedCourse.course_code,
          academic_year: academicYear,
          semester_type: semesterType,
        },
      });

      setAttainmentData(response.data);
    } catch (error) {
      console.error("Error fetching attainment data:", error);
      toast.error("Failed to load attainment data");
      setAttainmentData(null);
    } finally {
      setIsAttainmentLoading(false);
    }
  };

  // Fetch attainment data when course is selected
  useEffect(() => {
    if (selectedCourse && academicYear && semesterType) {
      fetchAttainmentData();
    }
  }, [selectedCourse, academicYear, semesterType]);

  // Fetch course sections using the new API
  const fetchCourseSections = async (course) => {
    setIsLoading(true);
    try {
      // Use the new API endpoint with POST request
      const response = await API.get("/ece/faculty/sections-by-academic-year", {
        params: {
          course_code: course.course_code,
          academic_year: academicYear,
        },
      });

      if (response.data) {
        setCourseDetails(response.data.course_info);
        setSections(response.data.sections || []);
        setSelectedCourse(course);
        setShowCourseList(false);
        toast.success("Course sections loaded successfully");
      }
    } catch (error) {
      console.error("Error fetching course sections:", error);
      toast.error("Failed to load course sections");
      setSections([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchOutcomesData = async () => {
      if (!selectedCourse || !academicYear) return;

      setIsOutcomesLoading(true);
      try {
        // Fetch course outcomes
        const coResponse = await API.get("ece/faculty/course-outcomes", {
          params: {
            course_code: selectedCourse.course_code,
            academic_year: academicYear,
          },
        });
        setCourseOutcomes(coResponse.data.course_outcomes || []);

        // Fetch program outcomes
        const poResponse = await API.get("ece/faculty/program-outcomes", {
          params: {
            program_code: selectedCourse.program_code,
            academic_year: academicYear,
          },
        });
        setProgramOutcomes(poResponse.data.program_outcomes || []);

        // Fetch program specific outcomes
        const psoResponse = await API.get(
          "ece/faculty/program-specific-outcomes",
          {
            params: {
              program_code: selectedCourse.program_code,
              academic_year: academicYear,
            },
          }
        );
        setProgramSpecificOutcomes(
          psoResponse.data.program_specific_outcomes || []
        );

        // Fetch articulation matrix
        try {
          const articulationResponse = await API.get(
            "ece/faculty/co-po-pso-matrix",
            {
              params: {
                course_code: selectedCourse.course_code,
                academic_year: academicYear,
              },
            }
          );
          setArticulationMatrix(articulationResponse.data);
        } catch (error) {
          // If no articulation matrix exists, set to null
          if (error.response?.status === 404) {
            setArticulationMatrix(null);
            console.log("No articulation matrix found for this course");
          } else {
            throw error;
          }
        }
      } catch (error) {
        toast.error("Failed to load outcomes data");
        console.error("Error fetching outcomes data:", error);
      } finally {
        setIsOutcomesLoading(false);
      }
    };

    fetchOutcomesData();
  }, [selectedCourse, academicYear]);

  // Go back to course selection
  const handleBackToCourses = () => {
    setShowCourseList(true);
    setSelectedCourse(null);
    setCourseDetails(null);
    setSections([]);
    setActiveView("final");
    setAttainmentData(null);
  };

  // Determine semester type display name
  const getSemesterTypeDisplayName = (type) => {
    return type === "odd" ? "Odd Semester" : "Even Semester";
  };

  const fetchCourses = async () => {
    if (!academicYear || !semesterType) {
      toast.error("Please select both academic year and semester type");
      return;
    }

    setIsLoading(true);
    try {
      const response = await API.get("/ece/faculty/courses", {
        params: {
          academic_year: academicYear,
          semester_type: semesterType,
          department_id: "ECE",
        },
      });

      if (response.data && response.data.subjects) {
        const flattenedCourses = Object.values(response.data.subjects).flat();
        setCourses(flattenedCourses);

        if (flattenedCourses.length === 0) {
          toast.error("No courses found for the selected criteria");
        } else {
          toast.success("Courses fetched successfully");
        }
      } else {
        setCourses([]);
        toast.error("No courses found for the selected criteria");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to fetch courses");
      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch courses on initial load
  useEffect(() => {
    fetchCourses();
  }, []);

  return (
    <>
      <AdminHeader />
      <div
        style={{
          backgroundColor: darkMode ? "#1E1E2E" : "#FFFFFF",
          minHeight: "100vh",
        }}
      >
        <div className="flex min-h-screen">
          <div className="flex-1 pt-4 overflow-y-auto px-4">
            {/* Header Section */}
            <div className="mb-4">
              <h2
                className="text-xl font-semibold"
                style={{ color: darkMode ? "#EAEAEA" : "#111827" }}
              >
                CO-PO Attainment Dashboard
              </h2>
              <p
                className="text-sm"
                style={{ color: darkMode ? "#B0B3B8" : "#374151" }}
              >
                View and analyze course attainment data
              </p>
            </div>

            {/* Filters Section */}
            <FiltersSection
              darkMode={darkMode}
              academicYear={academicYear}
              setAcademicYear={setAcademicYear}
              academicYears={academicYears}
              semesterType={semesterType}
              setSemesterType={setSemesterType}
              isLoading={isLoading}
              fetchCourses={fetchCourses}
              defaultAcademicYear={defaultAcademicYear}
            />

            {/* No Courses Message */}
            {showCourseList && courses.length === 0 && !isLoading && (
              <div
                className="rounded-lg p-6 text-center border"
                style={{
                  backgroundColor: darkMode ? "#0D1117" : "#F9FAFB",
                  borderColor: darkMode ? "#22232B" : "#D1D5DB",
                }}
              >
                <p style={{ color: darkMode ? "#B0B3B8" : "#4B5563" }}>
                  No courses found. Please adjust your filters and try again.
                </p>
              </div>
            )}

            {/* Course List */}
            {showCourseList && courses.length > 0 && (
              <div className="mt-4">
                <CourseList
                  darkMode={darkMode}
                  courses={courses}
                  semesterType={semesterType}
                  getSemesterTypeDisplayName={getSemesterTypeDisplayName}
                  fetchCourseSections={fetchCourseSections}
                />
              </div>
            )}

            {/* Course Details */}
            {selectedCourse && courseDetails && !showCourseList && (
              <div className="mt-4">
                {/* Back Button and Course Header */}
                <CourseHeader
                  darkMode={darkMode}
                  selectedCourse={selectedCourse}
                  courseDetails={courseDetails}
                  semesterType={semesterType}
                  getSemesterTypeDisplayName={getSemesterTypeDisplayName}
                  handleBackToCourses={handleBackToCourses}
                />

                {/* Faculty Sections */}
                <SectionsTable
                  darkMode={darkMode}
                  sections={sections}
                  courseDetails={courseDetails}
                />

                {/* Action Buttons */}
                <ActionButtons
                  darkMode={darkMode}
                  activeView={activeView}
                  setActiveView={setActiveView}
                />

                {/* Dynamic Views */}
                <div className="mt-3">
                  {activeView === "final" && (
                    <FinalCOPOView
                      course={selectedCourse}
                      academicYear={academicYear}
                      semesterType={semesterType}
                      darkMode={darkMode}
                      attainmentData={attainmentData}
                      isLoading={isAttainmentLoading}
                    />
                  )}
                  {activeView === "outcomes" && (
                    <OutcomesView
                      course={selectedCourse}
                      academicYear={academicYear}
                      outcomes={courseOutcomes}
                      programOutcomes={programOutcomes}
                      programSpecificOutcomes={programSpecificOutcomes}
                      articulationMatrix={articulationMatrix}
                      isLoading={isOutcomesLoading}
                      darkMode={darkMode}
                    />
                  )}
                  {activeView === "matrix" && (
                    <MatrixView
                      course={selectedCourse}
                      academicYear={academicYear}
                      articulationMatrix={articulationMatrix}
                      isLoading={isOutcomesLoading}
                      darkMode={darkMode}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminCOPO;
